import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Message,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import {
  MAX_TTL_MINUTES_V107,
  validTTLMinutesV107,
} from './cacheTTLMinutesV107';

const componentId = () =>
  `${Date.now()}:${Math.random().toString(16).slice(2)}`;

const CACHE_STORAGE_METER_V820 = 'cache_storage_tokens';
const CACHE_STORAGE_PERIOD_UNITS_V820 = ['second', 'minute', 'hour', 'day'];
const CACHE_STORAGE_ROUNDING_V820 = ['prorated', 'ceil_period'];

const unitForMeter = (meter) =>
  ({
    media_units: 'per_unit',
    search_requests: 'per_unit',
    scrape_pages: 'per_unit',
    search_first_result: 'per_unit',
    search_additional_results: 'per_unit',
    image: 'per_image',
    reference_images: 'per_image',
    audio_characters: 'per_million_characters',
    audio_duration: 'per_minute',
    video: 'per_video',
    video_duration: 'per_second',
    cache_storage_tokens: 'per_million_token_period',
  }[meter] || 'per_million_tokens');

const emptyComponent = (meter = 'input_tokens') => ({
  client_id: componentId(),
  meter,
  unit: unitForMeter(meter),
  unit_amount_usd: '',
  size: '',
  quality: '',
  resolution: '',
  aspect_ratio: '',
  ttl_minutes: '',
  period_value: meter === CACHE_STORAGE_METER_V820 ? '1' : '',
  period_unit: meter === CACHE_STORAGE_METER_V820 ? 'hour' : '',
  rounding: meter === CACHE_STORAGE_METER_V820 ? 'prorated' : '',
});

const emptyForm = () => ({
  cache_ttl_enabled: false,
  model_name: '',
  purpose: 'customer_list',
  note: '',
  components: [emptyComponent('input_tokens'), emptyComponent('output_tokens')],
});

const BillingPriceSettingV8 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const purposeOptions = useMemo(
    () => [
      {
        key: 'customer_list',
        value: 'customer_list',
        text: t('setting.billing.pricing.purpose.customer'),
      },
    ],
    [t]
  );

  const meterOptions = useMemo(
    () =>
      [
        'input_tokens',
        'input_text_tokens',
        'cached_input_tokens',
        'cache_storage_tokens',
        'cache_creation_tokens',
        'output_tokens',
        'image',
        'reference_images',
        'audio_characters',
        'audio_duration',
        'video',
        'video_duration',
      ].map((meter) => ({
        key: meter,
        value: meter,
        text: t(`setting.billing.pricing.meters.${meter}`),
      })),
    [t]
  );
  const cacheStoragePeriodUnitOptions = useMemo(
    () =>
      CACHE_STORAGE_PERIOD_UNITS_V820.map((unit) => ({
        key: unit,
        value: unit,
        text: t(`setting.billing.pricing.cache_storage.period_units.${unit}`),
      })),
    [t]
  );
  const cacheStorageRoundingOptions = useMemo(
    () =>
      CACHE_STORAGE_ROUNDING_V820.map((rounding) => ({
        key: rounding,
        value: rounding,
        text: t(
          `setting.billing.pricing.cache_storage.rounding_options.${rounding}`
        ),
      })),
    [t]
  );

  const modelOptions = useMemo(
    () =>
      models.map((modelName) => ({
        key: modelName,
        value: modelName,
        text: modelName,
      })),
    [models]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [modelResponse, versionResponse] = await Promise.all([
        API.get('/api/billing/v8/admin/price-models'),
        API.get(
          '/api/billing/v8/admin/price-versions?purpose=customer_list&page=1&page_size=100'
        ),
      ]);
      if (modelResponse?.data?.success) {
        setModels(modelResponse.data.data || []);
      }
      if (versionResponse?.data?.success) {
        setVersions(versionResponse.data.data?.items || []);
      }
    } catch (error) {
      showError(error.message || t('setting.billing.pricing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData().then();
  }, [loadData]);

  const updateComponent = (index, field, value) => {
    setForm((current) => ({
      ...current,
      components: current.components.map((component, componentIndex) => {
        if (componentIndex !== index) return component;
        if (field === 'meter') {
          return {
            ...component,
            meter: value,
            unit: unitForMeter(value),
            size: value === 'image' ? component.size : '',
            quality: value === 'image' ? component.quality : '',
            resolution: value === 'image' ? component.resolution : '',
            aspect_ratio: value === 'image' ? component.aspect_ratio : '',
            ttl_minutes:
              value === 'cache_creation_tokens' ? component.ttl_minutes : '',
            period_value:
              value === CACHE_STORAGE_METER_V820
                ? component.period_value || '1'
                : '',
            period_unit:
              value === CACHE_STORAGE_METER_V820
                ? component.period_unit || 'hour'
                : '',
            rounding:
              value === CACHE_STORAGE_METER_V820
                ? component.rounding || 'prorated'
                : '',
          };
        }
        return { ...component, [field]: value };
      }),
    }));
  };

  const removeComponent = (index) => {
    setForm((current) => ({
      ...current,
      components: current.components.filter(
        (_, componentIndex) => componentIndex !== index
      ),
    }));
  };

  const toggleCacheStorageV820 = (checked) => {
    setForm((current) => {
      const withoutStorage = current.components.filter(
        (component) => component.meter !== CACHE_STORAGE_METER_V820
      );
      if (!checked) return { ...current, components: withoutStorage };
      if (withoutStorage.length !== current.components.length) return current;
      const cachedInputIndex = withoutStorage.findIndex(
        (component) => component.meter === 'cached_input_tokens'
      );
      const cacheCreationIndex = withoutStorage.findIndex(
        (component) => component.meter === 'cache_creation_tokens'
      );
      const insertIndex =
        cachedInputIndex >= 0
          ? cachedInputIndex + 1
          : cacheCreationIndex >= 0
          ? cacheCreationIndex
          : withoutStorage.length;
      const components = [...withoutStorage];
      components.splice(
        insertIndex,
        0,
        emptyComponent(CACHE_STORAGE_METER_V820)
      );
      return { ...current, components };
    });
  };

  const createVersion = async () => {
    if (
      form.cache_ttl_enabled &&
      form.components.some(
        (component) =>
          component.meter === 'cache_creation_tokens' &&
          !validTTLMinutesV107(component.ttl_minutes)
      )
    ) {
      showError(t('models.commercial.cache_ttl.invalid'));
      return;
    }
    const cacheStorageComponents = form.components.filter(
      (component) => component.meter === CACHE_STORAGE_METER_V820
    );
    if (
      !form.model_name.trim() ||
      form.components.length === 0 ||
      form.components.some((component) => !component.unit_amount_usd.trim()) ||
      cacheStorageComponents.some(
        (component) =>
          !/^\d+$/.test(String(component.period_value).trim()) ||
          !Number.isSafeInteger(Number(component.period_value)) ||
          Number(component.period_value) <= 0 ||
          !CACHE_STORAGE_PERIOD_UNITS_V820.includes(component.period_unit) ||
          !CACHE_STORAGE_ROUNDING_V820.includes(component.rounding)
      )
    ) {
      showError(t('setting.billing.pricing.errors.invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await API.post('/api/billing/v8/admin/price-versions', {
        model_name: form.model_name.trim(),
        purpose: form.purpose,
        note: form.note,
        components: form.components.map((component) => {
          const attributes = {};
          if (
            component.meter === 'cache_creation_tokens' &&
            form.cache_ttl_enabled
          ) {
            attributes.ttl_minutes = Number(component.ttl_minutes);
          }
          if (component.meter === 'image') {
            if (component.size.trim()) attributes.size = component.size.trim();
            if (component.quality.trim())
              attributes.quality = component.quality.trim();
            if (component.resolution.trim())
              attributes.resolution = component.resolution.trim();
            if (component.aspect_ratio.trim())
              attributes.aspect_ratio = component.aspect_ratio.trim();
          }
          if (component.meter === CACHE_STORAGE_METER_V820) {
            attributes.period_value = Number(component.period_value);
            attributes.period_unit = component.period_unit;
            attributes.rounding = component.rounding;
          }
          return {
            meter: component.meter,
            unit: component.unit,
            unit_amount_usd: component.unit_amount_usd.trim(),
            attributes,
          };
        }),
      });
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(t('setting.billing.pricing.messages.created'));
      const savedModel = form.model_name.trim();
      const savedPurpose = form.purpose;
      setForm({
        ...emptyForm(),
        model_name: savedModel,
        purpose: savedPurpose,
      });
      setSelectedVersion(response.data.data);
      await loadData();
    } catch (error) {
      showError(error.message || t('setting.billing.pricing.errors.create'));
    } finally {
      setLoading(false);
    }
  };

  const inspectVersion = async (versionID) => {
    setLoading(true);
    try {
      const response = await API.get(
        `/api/billing/v8/admin/price-versions/${versionID}`
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setSelectedVersion(response.data.data);
    } catch (error) {
      showError(error.message || t('setting.billing.pricing.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form loading={loading}>
      <Header as='h3'>{t('setting.billing.pricing.editor.title')}</Header>
      <Form.Group widths='equal'>
        <Form.Field>
          <label>{t('setting.billing.pricing.fields.model')}</label>
          <Dropdown
            selection
            search
            fluid
            allowAdditions
            additionLabel=''
            options={modelOptions}
            value={form.model_name}
            onAddItem={(_, { value }) =>
              setModels((current) =>
                current.includes(value) ? current : [...current, value].sort()
              )
            }
            onChange={(_, { value }) =>
              setForm((current) => ({ ...current, model_name: value }))
            }
          />
        </Form.Field>
        <Form.Field>
          <label>{t('setting.billing.pricing.fields.purpose')}</label>
          <Dropdown
            selection
            fluid
            options={purposeOptions}
            value={form.purpose}
            onChange={(_, { value }) =>
              setForm((current) => ({ ...current, purpose: value }))
            }
          />
        </Form.Field>
      </Form.Group>

      <Form.Field>
        <Checkbox
          toggle
          label={t('setting.billing.pricing.cache_storage.enabled')}
          checked={form.components.some(
            (component) => component.meter === CACHE_STORAGE_METER_V820
          )}
          onChange={(_, data) => toggleCacheStorageV820(data.checked)}
        />
        <div style={{ color: '#767676', marginTop: '6px' }}>
          {t('setting.billing.pricing.cache_storage.help')}
        </div>
      </Form.Field>

      <div style={{ overflowX: 'auto' }}>
        <Form.Field>
          <Checkbox
            toggle
            label={t('models.commercial.cache_ttl.enabled')}
            checked={form.cache_ttl_enabled}
            onChange={(_, { checked }) =>
              setForm((current) => {
                let components = current.components.map((component) =>
                  component.meter === 'cache_creation_tokens'
                    ? {
                        ...component,
                        ttl_minutes: checked ? component.ttl_minutes : '',
                        unit_amount_usd: checked
                          ? component.unit_amount_usd
                          : '',
                      }
                    : component
                );
                if (
                  checked &&
                  !components.some(
                    (component) => component.meter === 'cache_creation_tokens'
                  )
                ) {
                  components = [
                    ...components,
                    emptyComponent('cache_creation_tokens'),
                  ];
                }
                return { ...current, cache_ttl_enabled: checked, components };
              })
            }
          />
        </Form.Field>
        <Table compact unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.meter')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.unit')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.price_usd')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.cache_storage.period_value')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.cache_storage.period_unit')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.cache_storage.rounding')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.image_size')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.image_quality')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.image_resolution')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.image_aspect_ratio')}
              </Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {form.components.map((component, index) => (
              <Table.Row key={component.client_id}>
                <Table.Cell>
                  <Dropdown
                    selection
                    options={meterOptions}
                    value={component.meter}
                    onChange={(_, { value }) =>
                      updateComponent(index, 'meter', value)
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  {t(`setting.billing.pricing.units.${component.unit}`)}
                </Table.Cell>
                <Table.Cell>
                  <Form.Input
                    inputMode='decimal'
                    value={component.unit_amount_usd}
                    onChange={(_, { value }) =>
                      updateComponent(index, 'unit_amount_usd', value)
                    }
                  />
                  {component.meter === 'cache_creation_tokens' &&
                  form.cache_ttl_enabled ? (
                    <Form.Input
                      type='number'
                      min='1'
                      max={MAX_TTL_MINUTES_V107}
                      step='1'
                      aria-label={t('models.commercial.cache_ttl.minutes')}
                      label={t('models.commercial.cache_ttl.minutes')}
                      value={component.ttl_minutes}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'ttl_minutes', value)
                      }
                    />
                  ) : null}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === CACHE_STORAGE_METER_V820 && (
                    <Form.Input
                      type='number'
                      min='1'
                      step='1'
                      value={component.period_value}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'period_value', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === CACHE_STORAGE_METER_V820 && (
                    <Dropdown
                      fluid
                      selection
                      options={cacheStoragePeriodUnitOptions}
                      value={component.period_unit}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'period_unit', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === CACHE_STORAGE_METER_V820 && (
                    <Dropdown
                      fluid
                      selection
                      options={cacheStorageRoundingOptions}
                      value={component.rounding}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'rounding', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === 'image' && (
                    <Form.Input
                      value={component.size}
                      maxLength={64}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'size', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === 'image' && (
                    <Form.Input
                      value={component.quality}
                      maxLength={64}
                      onChange={(_, { value }) =>
                        updateComponent(index, 'quality', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === 'image' && (
                    <Form.Input
                      value={component.resolution}
                      maxLength={64}
                      placeholder='1k / 2k'
                      onChange={(_, { value }) =>
                        updateComponent(index, 'resolution', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {component.meter === 'image' && (
                    <Form.Input
                      value={component.aspect_ratio}
                      maxLength={64}
                      placeholder='1:1 / 16:9'
                      onChange={(_, { value }) =>
                        updateComponent(index, 'aspect_ratio', value)
                      }
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    type='button'
                    icon='trash'
                    basic
                    negative
                    disabled={form.components.length === 1}
                    title={t(
                      'setting.billing.pricing.actions.remove_component'
                    )}
                    aria-label={t(
                      'setting.billing.pricing.actions.remove_component'
                    )}
                    onClick={() => removeComponent(index)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      <Button
        type='button'
        icon
        labelPosition='left'
        onClick={() =>
          setForm((current) => ({
            ...current,
            components: [...current.components, emptyComponent()],
          }))
        }
      >
        <Icon name='plus' />
        {t('setting.billing.pricing.actions.add_component')}
      </Button>
      <Form.TextArea
        label={t('setting.billing.fields.note')}
        value={form.note}
        maxLength={1000}
        onChange={(_, { value }) =>
          setForm((current) => ({ ...current, note: value }))
        }
      />
      <Button
        type='button'
        primary
        icon
        labelPosition='left'
        onClick={createVersion}
      >
        <Icon name='save' />
        {t('setting.billing.pricing.actions.create_version')}
      </Button>

      <Divider />
      <Header as='h3'>{t('setting.billing.pricing.list.title')}</Header>
      <div style={{ overflowX: 'auto' }}>
        <Table compact selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.model')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.purpose')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fields.version')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.list.components')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.effective_at')}
              </Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {versions.map((version) => (
              <Table.Row key={version.id}>
                <Table.Cell>{version.model_name}</Table.Cell>
                <Table.Cell>
                  {t(
                    `setting.billing.pricing.purpose.${
                      version.purpose === 'provider_cost'
                        ? 'provider'
                        : 'customer'
                    }`
                  )}
                </Table.Cell>
                <Table.Cell>{version.version}</Table.Cell>
                <Table.Cell>{version.component_count}</Table.Cell>
                <Table.Cell>
                  {new Date(version.effective_at * 1000).toLocaleString()}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    type='button'
                    icon='eye'
                    basic
                    title={t('setting.billing.pricing.actions.inspect')}
                    aria-label={t('setting.billing.pricing.actions.inspect')}
                    onClick={() => inspectVersion(version.id)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {selectedVersion && (
        <Message>
          <Message.Header>{`${selectedVersion.model_name} v${selectedVersion.version}`}</Message.Header>
          <Message.List>
            {(selectedVersion.components || []).map((component) => (
              <Message.Item key={component.id}>
                {t(`setting.billing.pricing.meters.${component.meter}`)}: USD{' '}
                {component.unit_amount_usd} /{' '}
                {t(`setting.billing.pricing.units.${component.unit}`)}
                {Object.keys(component.attributes || {}).length > 0
                  ? ` (${Object.entries(component.attributes)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')})`
                  : ''}
              </Message.Item>
            ))}
          </Message.List>
        </Message>
      )}
    </Form>
  );
};

export default BillingPriceSettingV8;
